// @ts-nocheck
import React from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  MoreVertical, 
  Mail, 
  Phone, 
  Calendar, 
  Globe, 
  Users, 
  Target,
  MessageSquare,
  CheckCircle 
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

interface AffiliateProspect {
  id: string;
  sales_rep_id: string;
  name: string;
  email: string;
  company_name?: string;
  website_url?: string;
  social_media_handles?: any;
  audience_size?: number;
  niche_focus?: string;
  lead_source: string;
  pipeline_stage: string;
  notes?: string;
  contact_date?: string;
  next_follow_up?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

interface AffiliatePipelineProps {
  prospects: AffiliateProspect[];
  onProspectEdit: (prospect: AffiliateProspect) => void;
  onRefresh: () => void;
}

const pipelineStages = [
  { id: 'prospect', name: 'Prospect', color: 'bg-gray-100 border-gray-300' },
  { id: 'application', name: 'Application', color: 'bg-blue-100 border-blue-300' },
  { id: 'review', name: 'Review', color: 'bg-yellow-100 border-yellow-300' },
  { id: 'approved', name: 'Approved', color: 'bg-green-100 border-green-300' },
  { id: 'active', name: 'Active', color: 'bg-emerald-100 border-emerald-300' },
  { id: 'inactive', name: 'Inactive', color: 'bg-red-100 border-red-300' }
];

const AffiliatePipeline: React.FC<AffiliatePipelineProps> = ({ 
  prospects, 
  onProspectEdit, 
  onRefresh 
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeProspect, setActiveProspect] = React.useState<AffiliateProspect | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const prospect = prospects.find(p => p.id === active.id);
    
    setActiveId(active.id as string);
    setActiveProspect(prospect || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || !active) return;
    
    const prospectId = active.id as string;
    const newStage = over.id as string;
    
    try {
      const { error } = await supabase
        .from('affiliate_prospects')
        .update({ 
          pipeline_stage: newStage,
          updated_at: new Date().toISOString()
        })
        .eq('id', prospectId);

      if (error) throw error;

      toast.success(`Prospect moved to ${pipelineStages.find(s => s.id === newStage)?.name}`);
      onRefresh();
    } catch (error) {
      console.error('Error updating prospect stage:', error);
      toast.error('Failed to update prospect stage');
    }
    
    setActiveId(null);
    setActiveProspect(null);
  };

  const getProspectsForStage = (stageId: string) => {
    return prospects.filter(prospect => prospect.pipeline_stage === stageId);
  };

  const getLeadSourceColor = (source: string) => {
    const colors: Record<string, string> = {
      manual: 'bg-gray-100 text-gray-800',
      referral: 'bg-green-100 text-green-800',
      website: 'bg-blue-100 text-blue-800',
      social_media: 'bg-pink-100 text-pink-800',
      event: 'bg-purple-100 text-purple-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  const formatAudienceSize = (size?: number) => {
    if (!size) return 'Unknown';
    if (size >= 1000000) return `${(size / 1000000).toFixed(1)}M`;
    if (size >= 1000) return `${(size / 1000).toFixed(1)}K`;
    return size.toString();
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 min-h-[600px]">
        {pipelineStages.map((stage) => {
          const stageProspects = getProspectsForStage(stage.id);
          const totalAudienceSize = stageProspects.reduce((sum, p) => sum + (p.audience_size || 0), 0);
          
          return (
            <Card key={stage.id} className={`flex flex-col border-t-4 ${stage.color}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    {stage.name}
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    {stageProspects.length}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {formatAudienceSize(totalAudienceSize)} total reach
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 pt-0">
                <div className="space-y-3 min-h-[200px] pb-4">
                  {stageProspects.map((prospect) => (
                    <Card 
                      key={prospect.id}
                      className="group hover:shadow-md transition-all duration-200 cursor-pointer border-l-2 border-l-primary/20 hover:border-l-primary"
                      draggable
                      onDragStart={() => setActiveProspect(prospect)}
                    >
                      <CardContent className="p-3">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm truncate text-foreground">
                              {prospect.name}
                            </h4>
                            {prospect.company_name && (
                              <p className="text-xs text-muted-foreground truncate">
                                {prospect.company_name}
                              </p>
                            )}
                          </div>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => onProspectEdit(prospect)}>
                                Edit Prospect
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Mail className="h-4 w-4 mr-2" />
                                Send Email
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Phone className="h-4 w-4 mr-2" />
                                Schedule Call
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Add Note
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 mb-3">
                          {prospect.audience_size && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Users className="h-3 w-3" />
                              <span>{formatAudienceSize(prospect.audience_size)} followers</span>
                            </div>
                          )}
                          
                          {prospect.niche_focus && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Target className="h-3 w-3" />
                              <span className="truncate">{prospect.niche_focus}</span>
                            </div>
                          )}
                          
                          {prospect.website_url && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Globe className="h-3 w-3" />
                              <span className="truncate">{prospect.website_url}</span>
                            </div>
                          )}
                        </div>

                        {/* Source and Time */}
                        <div className="flex items-center justify-between mb-3">
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${getLeadSourceColor(prospect.lead_source)}`}
                          >
                            {prospect.lead_source.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(prospect.updated_at), { addSuffix: true })}
                          </span>
                        </div>

                        {/* Tags */}
                        {prospect.tags && prospect.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {prospect.tags.slice(0, 2).map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {prospect.tags.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{prospect.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Email">
                            <Mail className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Call">
                            <Phone className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Schedule">
                            <Calendar className="h-3 w-3" />
                          </Button>
                          {prospect.website_url && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Visit Website">
                              <Globe className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        {/* Next follow-up indicator */}
                        {prospect.next_follow_up && (
                          <div className="mt-2 pt-2 border-t border-muted">
                            <div className="flex items-center gap-1 text-xs text-orange-600">
                              <Calendar className="h-3 w-3" />
                              <span>Follow up: {format(new Date(prospect.next_follow_up), 'MMM dd')}</span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  
                  {stageProspects.length === 0 && (
                    <div className="text-center text-muted-foreground text-sm py-8 border-2 border-dashed border-muted-foreground/20 rounded-lg">
                      <p>No prospects in this stage</p>
                      <p className="text-xs mt-1">Drag prospects here</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <DragOverlay>
        {activeProspect ? (
          <Card className="rotate-2 opacity-80">
            <CardContent className="p-3">
              <h4 className="font-semibold text-sm">{activeProspect.name}</h4>
              {activeProspect.company_name && (
                <p className="text-xs text-muted-foreground">{activeProspect.company_name}</p>
              )}
            </CardContent>
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default AffiliatePipeline;